import { supabase } from '../utils/supabase';
import { fetchPlayerRecentSessions } from './playerService';
import { isPrivateSession, isPegBoardSession } from '../utils/sessionTypeUtils';
import { canViewSessionInBrowse } from '../utils/sessionVisibilityUtils';
import { fetchSessionLeaderboardSnapshotRecap, fetchSessionSnapshotPairs } from './leaderboardSnapshotService';
import { fetchPlayerSessionDatesForIds, fetchViewerActiveLadderTeaser } from './clubLeaderboardService';
import { fetchRecentSnapshotsForScope } from './leaderboardMovementService';
import { LEADERBOARD_SCOPES } from '../utils/leaderboardPlayerScope';
import {
  activeRankAndDeltaForPlayer,
  applyActiveScopedRanks,
  collectPlayerIdsFromRecentSnapshots,
} from '../utils/leaderboardSnapshotUtils';
import {
  buildSessionLadderSnapshot,
  computeMaxWinStreakInSession,
  summarizeLadderGameResults,
} from '../utils/sessionSnapshotUtils';
import {
  mapSessionToHomeItem,
  sortSessionsByDateTime,
  sortOngoingForNextGame,
  filterUpcomingSessions,
  deduplicateSessions,
  isSessionDateWithinDays,
} from '../utils/homeSessionUtils';
import { SESSION_ROSTER_STATUSES } from '../utils/sessionRosterUtils';
import { MIXED_LEVELS_GAME_TYPE_IDS, winRatePctFromRecord } from '../utils/winRate';

/** Lean session shape for Home cards (avoid description, players, tournaments). */
const HOME_SESSION_CARD_SELECT = `
  id,
  name,
  session_date,
  start_time,
  end_time,
  session_status,
  session_type_id,
  club_id,
  host_player_id,
  max_players,
  is_public,
  waitlist_enabled,
  signup_selection,
  session_types:session_type_id(id, name),
  clubs:club_id(id, name, banner_image_url, logo_image_url)
`;

const TERMINAL = new Set(['completed', 'archived', 'cancelled']);

/**
 * Replaces full-club snapshot rank and rank change with the Active cohort
 * (players with a session in the 60 days before each snapshot).
 *
 * Uses this session's snapshot and the previous one when they exist, so the
 * hero describes that session. Ladders with no session snapshot fall back to
 * the latest two Active-scoped snapshots for the current season.
 */
async function applyActivePlayerRanks(ladderSnapshot, { playerId, clubId, sessionId }) {
  const ladderTypes = [ladderSnapshot.mixed?.key, ladderSnapshot.levels?.key].filter(Boolean);
  if (!ladderTypes.length) return ladderSnapshot;

  const pairs = sessionId
    ? await fetchSessionSnapshotPairs(clubId, sessionId, ladderTypes)
    : {};

  const missing = ladderTypes.filter((ladderType) => !pairs[ladderType]?.current);
  if (missing.length > 0) {
    const seasonMap = await batchResolveClubSeasons([clubId]);
    const seasonId = seasonMap.get(clubId);
    if (seasonId) {
      await Promise.all(
        missing.map(async (ladderType) => {
          const recent = await fetchRecentSnapshotsForScope(
            clubId,
            seasonId,
            ladderType,
            LEADERBOARD_SCOPES.ACTIVE
          );
          pairs[ladderType] = { ...recent, seasonId };
        })
      );
    }
  }

  const idsBySeason = new Map();
  ladderTypes.forEach((ladderType) => {
    const pair = pairs[ladderType];
    if (!pair?.current?.snapshot_at || !pair.seasonId) return;
    const ids = collectPlayerIdsFromRecentSnapshots([pair]);
    if (!idsBySeason.has(pair.seasonId)) idsBySeason.set(pair.seasonId, new Set());
    ids.forEach((id) => idsBySeason.get(pair.seasonId).add(id));
  });

  const datesBySeason = new Map();
  await Promise.all(
    [...idsBySeason.entries()].map(async ([seasonId, idSet]) => {
      const dates = await fetchPlayerSessionDatesForIds([...idSet], clubId, seasonId);
      datesBySeason.set(seasonId, dates);
    })
  );

  const ranksByLadderType = {};
  ladderTypes.forEach((ladderType) => {
    const pair = pairs[ladderType];
    if (!pair?.current?.snapshot_at || !pair.seasonId) return;
    const scoped = activeRankAndDeltaForPlayer({
      playerId,
      currentEntries: pair.current.entries,
      previousEntries: pair.previous?.entries ?? [],
      currentSnapshotAt: pair.current.snapshot_at,
      previousSnapshotAt: pair.previous?.snapshot_at ?? null,
      sessionDatesByPlayerId: datesBySeason.get(pair.seasonId) ?? {},
    });
    if (scoped) ranksByLadderType[ladderType] = scoped;
  });

  return applyActiveScopedRanks(ladderSnapshot, ranksByLadderType);
}

/**
 * Fills a missing Elo rating from live club ratings, then replaces rank and
 * rank change with the Active-player leaderboard (same cohort as the club default).
 * Mutates and returns the same ladderSnapshot object.
 */
export async function enrichSessionLadderSnapshot(
  ladderSnapshot,
  { playerId, clubId, sessionId }
) {
  if (!ladderSnapshot || !playerId || !clubId) return ladderSnapshot;

  const mixedLadder = ladderSnapshot.mixed;
  const levelsLadder = ladderSnapshot.levels;

  if (mixedLadder?.eloRating == null || levelsLadder?.eloRating == null) {
    const { data: eloRow } = await supabase
      .from('player_club_elo_ratings')
      .select('mixed_elo_rating, levels_elo_rating')
      .eq('player_id', playerId)
      .eq('club_id', clubId)
      .maybeSingle();

    if (eloRow) {
      if (mixedLadder && mixedLadder.eloRating == null) {
        mixedLadder.eloRating = eloRow.mixed_elo_rating ?? null;
      }
      if (levelsLadder && levelsLadder.eloRating == null) {
        levelsLadder.eloRating = eloRow.levels_elo_rating ?? null;
      }
    }
  }

  try {
    await applyActivePlayerRanks(ladderSnapshot, { playerId, clubId, sessionId });
  } catch (err) {
    console.warn('Active leaderboard rank for session hero failed:', err);
  }

  return ladderSnapshot;
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

/** Match Home rules: ongoing always; upcoming only if date not in the past. */
export function filterSessionsForHomeBrowse(rows) {
  const t = todayStr();
  return (rows || []).filter((s) => {
    if (!s?.session_status || TERMINAL.has(s.session_status)) return false;
    if (s.session_status === 'ongoing') return true;
    if (s.session_status !== 'upcoming') return false;
    return !s.session_date || s.session_date >= t;
  });
}

export async function fetchHomeMemberships(playerId, { includeInactive = false } = {}) {
  let query = supabase
    .from('players_club')
    .select('club_id, club_role, is_active, membership_status, is_trial, reactivation_requested_at, created_at, clubs:club_id(id, name, location, banner_image_url, logo_image_url)')
    .eq('player_id', playerId)
    .neq('club_role', 'guest')
    .order('created_at', { ascending: true });

  if (includeInactive) {
    query = query.or('is_active.eq.true,membership_status.eq.inactive');
  } else {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching club memberships:', error);
    throw error;
  }
  return data || [];
}

export function deriveQuickActionsFromMemberships(clubMemberships) {
  const canCreateSession = clubMemberships.some((m) =>
    ['host', 'admin', 'owner'].includes(m.club_role)
  );
  const hostedClubs = clubMemberships
    .filter((m) => ['host', 'admin', 'owner'].includes(m.club_role))
    .sort((a, b) => (a.clubs?.name || '').localeCompare(b.clubs?.name || ''));
  const clubsForSessionCreate = hostedClubs.map((m) => ({
    clubId: m.club_id,
    name: m.clubs?.name || 'Unknown Club',
    location: m.clubs?.location ?? null,
  }));
  return { canCreateSession, clubsForSessionCreate };
}

async function fetchHostedSessionsForHome(playerId) {
  const { data, error } = await supabase
    .from('sessions')
    .select(HOME_SESSION_CARD_SELECT)
    .eq('host_player_id', playerId)
    .in('session_status', ['upcoming', 'ongoing']);

  if (error) throw error;
  return filterSessionsForHomeBrowse(data || []);
}

/**
 * Session rows for Home browse: same breadth as legacy getAllViewableSessions
 * (all clubs), without per-session signup N+1. Chunked to stay within PostgREST limits.
 */
async function fetchAllViewableSessionsForHomeLean() {
  const { data: clubsData, error: clubsError } = await supabase.from('clubs').select('id');
  if (clubsError) throw clubsError;
  const clubIds = (clubsData || []).map((c) => c.id);
  if (!clubIds.length) return [];

  const CHUNK = 100;
  const chunks = [];
  for (let i = 0; i < clubIds.length; i += CHUNK) {
    chunks.push(clubIds.slice(i, i + CHUNK));
  }

  const rows = await Promise.all(
    chunks.map((ids) =>
      supabase
        .from('sessions')
        .select(HOME_SESSION_CARD_SELECT)
        .in('club_id', ids)
        .in('session_status', ['upcoming', 'ongoing'])
        .not('session_date', 'is', null)
        .neq('session_status', 'cancelled')
        .then(({ data, error }) => {
          if (error) throw error;
          return data || [];
        })
    )
  );

  return filterSessionsForHomeBrowse(rows.flat());
}

/** Upcoming browse cards for discovery/onboarding — sessions the user hasn’t committed to yet. */
function pickDiscoveryRecommendations(sortedUpcomingRaw, mapOne, memberClubIds) {
  const userHasClubs = memberClubIds.size > 0;
  const seen = new Set();
  const candidates = [];

  for (const raw of sortedUpcomingRaw) {
    if (raw.session_status !== 'upcoming') continue;
    const mapped = mapOne(raw);
    if (!canViewSessionInBrowse(mapped, { memberClubIds })) continue;
    if (mapped.userRelationship !== 'none') continue;
    if (seen.has(mapped.id)) continue;
    seen.add(mapped.id);
    candidates.push(mapped);
  }

  candidates.sort((a, b) => {
    if (userHasClubs) {
      const ac = memberClubIds.has(a.club_id);
      const bc = memberClubIds.has(b.club_id);
      if (ac !== bc) return ac ? -1 : 1;
    } else {
      const ap = a.is_public ? 1 : 0;
      const bp = b.is_public ? 1 : 0;
      if (ap !== bp) return bp - ap;
    }
    const dcmp = (a.session_date || '').localeCompare(b.session_date || '');
    if (dcmp !== 0) return dcmp;
    return (a.start_time || '').localeCompare(b.start_time || '');
  });

  const widen = [];
  const seenW = new Set(candidates.map((c) => c.id));
  for (const raw of sortedUpcomingRaw) {
    if (raw.session_status !== 'upcoming') continue;
    const mapped = mapOne(raw);
    if (!canViewSessionInBrowse(mapped, { memberClubIds })) continue;
    if (['player', 'both'].includes(mapped.userRelationship)) continue;
    if (seenW.has(mapped.id)) continue;
    seenW.add(mapped.id);
    widen.push(mapped);
  }
  widen.sort((a, b) => {
    if (userHasClubs) {
      const ac = memberClubIds.has(a.club_id);
      const bc = memberClubIds.has(b.club_id);
      if (ac !== bc) return ac ? -1 : 1;
    } else {
      const ap = a.is_public ? 1 : 0;
      const bp = b.is_public ? 1 : 0;
      if (ap !== bp) return bp - ap;
    }
    const dcmp = (a.session_date || '').localeCompare(b.session_date || '');
    if (dcmp !== 0) return dcmp;
    return (a.start_time || '').localeCompare(b.start_time || '');
  });

  const merged = [...candidates, ...widen];
  const seenFinal = new Set();
  const out = [];
  for (const c of merged) {
    if (seenFinal.has(c.id)) continue;
    seenFinal.add(c.id);
    out.push(c);
    if (out.length >= 12) break;
  }
  return out;
}

async function buildSignupCountsMap(sessionIds) {
  const map = new Map();
  if (!sessionIds.length) return map;
  const { data, error } = await supabase
    .from('session_signups')
    .select('session_id, signup_status')
    .in('session_id', sessionIds);

  if (error) throw error;
  (data || []).forEach((signup) => {
    if (!map.has(signup.session_id)) {
      map.set(signup.session_id, { confirmed: 0, waitlist: 0 });
    }
    const c = map.get(signup.session_id);
    if (signup.signup_status === 'confirmed') c.confirmed++;
    if (signup.signup_status === 'waitlist') c.waitlist++;
  });
  return map;
}

async function buildSessionRosterCountsMap(sessionIds) {
  const map = new Map();
  if (!sessionIds.length) return map;

  const { data, error } = await supabase
    .from('session_players')
    .select('session_id')
    .in('session_id', sessionIds)
    .in('status', SESSION_ROSTER_STATUSES);

  if (error) throw error;

  (data || []).forEach((row) => {
    map.set(row.session_id, (map.get(row.session_id) || 0) + 1);
  });
  return map;
}

/**
 * Next game + happening soon. Replaces getAllViewableSessions on Home (no per-club N+1 signups).
 * `nextGame` is the earliest session the player is committed to (host, signed up, waitlisted, or in session_players).
 */
export async function fetchHomeSessionDeck(playerId, siteRole, memberships) {
  const clubRolesMap = new Map(memberships.map((m) => [m.club_id, m.club_role]));
  const memberClubIds = new Set((memberships || []).map((m) => m.club_id));

  const [
    sessionPlayerRows,
    signupRows,
    hostedSessions,
    viewableSessions,
  ] = await Promise.all([
    supabase
      .from('session_players')
      .select('session_id, status')
      .eq('player_id', playerId)
      .in('status', ['active', 'signed_up', 'paused'])
      .then(({ data }) => data || []),
    supabase
      .from('session_signups')
      .select('session_id, signup_status, waitlist_position, signup_date')
      .eq('player_id', playerId)
      .in('signup_status', ['confirmed', 'waitlist'])
      .then(({ data }) => data || []),
    fetchHostedSessionsForHome(playerId),
    fetchAllViewableSessionsForHomeLean(),
  ]);

  const signupMap = new Map();
  signupRows.forEach((signup) => {
    signupMap.set(signup.session_id, signup);
  });

  const participantSessionIdsSet = new Set(sessionPlayerRows.map((row) => row.session_id));

  const participantSessionIds = sessionPlayerRows.map((r) => r.session_id);
  const signupSessionIds = signupRows.map((r) => r.session_id);
  const relevantSessionIds = [...new Set([...participantSessionIds, ...signupSessionIds])];

  const allSessionIds = [
    ...new Set([
      ...relevantSessionIds,
      ...hostedSessions.map((s) => s.id),
      ...viewableSessions.map((s) => s.id),
    ]),
  ];

  const [participantSessionRows, signupCountsMap] = await Promise.all([
    relevantSessionIds.length
      ? supabase
          .from('sessions')
          .select(HOME_SESSION_CARD_SELECT)
          .in('id', relevantSessionIds)
          .then(({ data, error }) => {
            if (error) throw error;
            return data || [];
          })
      : Promise.resolve([]),
    buildSignupCountsMap(allSessionIds),
  ]);

  const enrichWithCounts = (session) => {
    const counts = signupCountsMap.get(session.id) || { confirmed: 0, waitlist: 0 };
    return {
      ...session,
      confirmed_players: counts.confirmed,
      waitlist_players: counts.waitlist,
    };
  };

  let participantSessions = participantSessionRows.map(enrichWithCounts);
  const enrichedHostedSessions = hostedSessions.map(enrichWithCounts);
  const enrichedViewableSessions = viewableSessions.map(enrichWithCounts);

  const allUserSessions = deduplicateSessions([
    ...participantSessions,
    ...enrichedHostedSessions,
    ...enrichedViewableSessions,
  ]);

  const isUserCommittedToSession = (session) => {
    if (session.host_player_id === playerId) return true;
    if (participantSessionIdsSet.has(session.id)) return true;
    const signup = signupMap.get(session.id);
    return Boolean(signup && ['confirmed', 'waitlist'].includes(signup.signup_status));
  };

  const ongoingEligible = allUserSessions.filter(
    (s) =>
      !isPrivateSession(s) &&
      s.session_status === 'ongoing' &&
      isUserCommittedToSession(s)
  );

  const isBrowsableUpcoming = (session) =>
    canViewSessionInBrowse(session, {
      memberClubIds,
      isCommitted: isUserCommittedToSession(session),
    });

  const upcomingSessions = filterUpcomingSessions(allUserSessions).filter(isBrowsableUpcoming);
  const sortedUpcomingRaw = sortSessionsByDateTime(upcomingSessions);
  const personalUpcomingSessions = sortedUpcomingRaw.filter(isUserCommittedToSession);

  const ongoingSessionIds = allUserSessions
    .filter((s) => s.session_status === 'ongoing')
    .map((s) => s.id);
  const rosterCountsMap = await buildSessionRosterCountsMap(ongoingSessionIds);

  const attachRosterCount = (session) => ({
    ...session,
    players_in_session:
      session.session_status === 'ongoing'
        ? rosterCountsMap.get(session.id) ?? 0
        : undefined,
  });

  const mapOne = (session) =>
    mapSessionToHomeItem(
      attachRosterCount(session),
      playerId,
      siteRole,
      clubRolesMap,
      signupMap,
      sessionPlayerRows
    );

  let nextGameData = null;
  let happeningSoonData = [];

  if (ongoingEligible.length > 0) {
    const chosenOngoing = sortOngoingForNextGame(ongoingEligible)[0];
    nextGameData = mapOne(chosenOngoing);
    happeningSoonData = sortedUpcomingRaw
      .filter((s) => s.id !== chosenOngoing.id)
      .slice(0, 6)
      .map(mapOne);
  } else if (personalUpcomingSessions.length > 0) {
    nextGameData = mapOne(personalUpcomingSessions[0]);
    happeningSoonData = sortedUpcomingRaw
      .filter((s) => s.id !== nextGameData.id)
      .slice(0, 6)
      .map(mapOne);
  }

  const discoverySessions = pickDiscoveryRecommendations(sortedUpcomingRaw, mapOne, memberClubIds);
  const upcomingInTwoWeeks = sortedUpcomingRaw
    .filter((session) => isSessionDateWithinDays(session, 14))
    .map(mapOne);

  return {
    nextGame: nextGameData,
    happeningSoon: happeningSoonData,
    discoverySessions,
    upcomingInTwoWeeks,
  };
}

/** Max club cards on the home dashboard strip (horizontal scroll on larger screens). */
const HOME_CLUBS_DISPLAY_CAP = 6;

/**
 * Latest `created_at` per club from `player_game_history` (desc scan — first hit per club wins).
 * @returns {Promise<Map<string, string>>} club_id → ISO timestamp
 */
async function fetchPlayerClubLastPlayedMap(playerId, clubIds) {
  const uniqueIds = [...new Set(clubIds)].filter(Boolean);
  if (!playerId || !uniqueIds.length) return new Map();

  const map = new Map();
  let offset = 0;
  const pageSize = 900;

  for (;;) {
    const { data, error } = await supabase
      .from('player_game_history')
      .select('club_id, created_at')
      .eq('player_id', playerId)
      .in('club_id', uniqueIds)
      .order('created_at', { ascending: false })
      .range(offset, offset + pageSize - 1);

    if (error) throw error;

    const batch = data || [];
    for (const row of batch) {
      if (row.club_id && !map.has(row.club_id)) {
        map.set(row.club_id, row.created_at);
      }
    }

    if (batch.length < pageSize || map.size >= uniqueIds.length) break;
    offset += pageSize;
  }

  return map;
}

/**
 * Club snapshot for the home dashboard — recently played clubs first (by last game at that club),
 * falling back to other memberships when the player has no recorded games yet.
 *
 * @returns {Promise<{ clubs: object[] }>}
 */
export async function fetchHomeClubsSnapshot(memberships, playerId, { limit = HOME_CLUBS_DISPLAY_CAP } = {}) {
  const clubIds = memberships.map((m) => m.club_id);
  let nextSessionsMap = new Map();

  if (clubIds.length > 0) {
    const td = todayStr();
    const { data: clubSessions } = await supabase
      .from('sessions')
      .select('id, club_id, session_date, start_time, name, session_status')
      .in('club_id', clubIds)
      .eq('session_status', 'upcoming')
      .gte('session_date', td)
      .order('session_date', { ascending: true })
      .order('start_time', { ascending: true });

    (clubSessions || []).forEach((session) => {
      if (!nextSessionsMap.has(session.club_id)) {
        nextSessionsMap.set(session.club_id, session);
      }
    });
  }

  let lastPlayedMap = new Map();
  if (playerId && clubIds.length > 0) {
    try {
      lastPlayedMap = await fetchPlayerClubLastPlayedMap(playerId, clubIds);
    } catch (err) {
      console.warn('fetchPlayerClubLastPlayedMap:', err);
    }
  }

  const clubsWithMeta = memberships.map((membership) => ({
    id: membership.club_id,
    name: membership.clubs?.name || 'Unknown Club',
    location: membership.clubs?.location || null,
    bannerImageUrl: membership.clubs?.banner_image_url || null,
    logoImageUrl: membership.clubs?.logo_image_url || null,
    role: membership.club_role,
    membershipStatus: membership.membership_status || 'active',
    isTrial: Boolean(membership.is_trial),
    reactivationRequestedAt: membership.reactivation_requested_at || null,
    nextSession: nextSessionsMap.get(membership.club_id) || null,
    lastPlayedAt: lastPlayedMap.get(membership.club_id) ?? null,
  }));

  const sorted = clubsWithMeta.slice().sort((a, b) => {
    const ta = a.lastPlayedAt ? new Date(a.lastPlayedAt).getTime() : 0;
    const tb = b.lastPlayedAt ? new Date(b.lastPlayedAt).getTime() : 0;
    if (tb !== ta) return tb - ta;
    return a.name.localeCompare(b.name);
  });

  return { clubs: limit == null ? sorted : sorted.slice(0, limit) };
}

/**
 * Count of clubs the API exposes to the signed-in user (respects `clubs` RLS).
 */
export async function fetchAppClubsCount() {
  const { count, error } = await supabase.from('clubs').select('id', {
    count: 'exact',
    head: true
  });

  if (error) {
    console.error('fetchAppClubsCount:', error);
    throw error;
  }
  return typeof count === 'number' ? count : 0;
}

export function fetchHomeActivity(playerId, limit = 5) {
  if (!playerId) return Promise.resolve([]);
  return fetchPlayerRecentSessions(playerId, limit, {
    includeEloDetails: false,
  }).then((rows) => (Array.isArray(rows) ? rows : []));
}

/**
 * Fetches all data needed for the Last Session Snapshot homepage hero in one composed call:
 * - Last completed session (W/L, Elo deltas from elo_history)
 * - Leaderboard snapshot recap for rank + snapshot-level Elo delta (competitive only)
 * - Ordered game results for computing the session's max win streak
 * - Pair-type counts to decide which ladder (Mixed / Levels) to feature (competitive only)
 *
 * Peg Board sessions return `variant: 'pegBoard'` with games / W–L / win rate / streak only.
 *
 * Returns null when the player has no completed sessions.
 *
 * @param {string} playerId
 * @param {'male'|'female'|null|undefined} viewerGender
 * @returns {Promise<object|null>}
 */
export async function fetchHomeLastSessionSnapshot(playerId, viewerGender) {
  if (!playerId) return null;

  const sessions = await fetchPlayerRecentSessions(playerId, 1, { includeEloDetails: true });
  if (!sessions || sessions.length === 0) return null;

  const session = sessions[0];
  const {
    sessionId,
    sessionName,
    sessionDate,
    clubId,
    clubName,
    clubLogoImageUrl,
    gamesPlayed,
    wins,
    losses,
    mixedEloDelta,
    levelsEloDelta,
    sessionTypeName,
  } = session;

  const { data: sgDataRaw } = await supabase
    .from('session_games')
    .select('id, created_at')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true });
  const sgData = sgDataRaw || [];

  let mixedResults = [];
  let levelsResults = [];
  let orderedResults = [];

  if (sgData.length > 0) {
    const sgIds = sgData.map((g) => g.id);
    const sgCreatedAt = new Map(sgData.map((g) => [g.id, g.created_at]));

    const { data: psgData } = await supabase
      .from('players_session_games')
      .select('session_game_id, result, pair_type')
      .eq('player_id', playerId)
      .in('session_game_id', sgIds);

    const sorted = (psgData || []).slice().sort((a, b) => {
      const ta = sgCreatedAt.get(a.session_game_id) || '';
      const tb = sgCreatedAt.get(b.session_game_id) || '';
      return ta < tb ? -1 : ta > tb ? 1 : 0;
    });

    orderedResults = sorted.map((row) => row.result);
    for (const row of sorted) {
      if (row.pair_type === 'levels') {
        levelsResults.push(row.result);
      } else {
        mixedResults.push(row.result);
      }
    }
  }

  const winRatePct = gamesPlayed > 0 ? Math.round((wins / gamesPlayed) * 100) : null;

  const sessionMeta = {
    sessionId,
    sessionName,
    sessionDate,
    clubId,
    clubName,
    clubLogoImageUrl: clubLogoImageUrl || null,
    gamesPlayed,
    wins,
    losses,
    winRatePct,
  };

  if (isPegBoardSession(sessionTypeName)) {
    return {
      ...sessionMeta,
      variant: 'pegBoard',
      maxWinStreak: computeMaxWinStreakInSession(orderedResults),
    };
  }

  const snapshotRecap = await fetchSessionLeaderboardSnapshotRecap(sessionId, playerId, clubId).catch(
    () => []
  );

  const mixedStats = summarizeLadderGameResults(mixedResults);
  const levelsStats = summarizeLadderGameResults(levelsResults);
  const maxWinStreak = computeMaxWinStreakInSession([...mixedResults, ...levelsResults]);

  const ladderSnapshot = buildSessionLadderSnapshot({
    mixedStats,
    levelsStats,
    mixedEloDelta: mixedEloDelta || 0,
    levelsEloDelta: levelsEloDelta || 0,
    snapshotRecap,
    viewerGender,
  });

  await enrichSessionLadderSnapshot(ladderSnapshot, { playerId, clubId, sessionId });

  const { mixed: mixedLadder, levels: levelsLadder, defaultLadderKey, canToggleLadder } = ladderSnapshot;

  const primaryLadder =
    defaultLadderKey === levelsLadder?.key ? levelsLadder : mixedLadder ?? levelsLadder;

  return {
    ...sessionMeta,
    variant: 'competitive',
    maxWinStreak,
    ladders: { mixed: mixedLadder, levels: levelsLadder },
    defaultLadderKey,
    canToggleLadder,
    primaryLadder,
  };
}

/**
 * Resolves the current season ID for each club in one or two queries.
 * Prefers season_status = 'current'; falls back to the latest by season_number.
 *
 * @param {string[]} clubIds
 * @returns {Promise<Map<string, string>>}  club_id → season_id
 */
export async function batchResolveClubSeasons(clubIds) {
  if (!clubIds.length) return new Map();

  const map = new Map();

  const { data: currentSeasons } = await supabase
    .from('seasons')
    .select('id, club_id')
    .in('club_id', clubIds)
    .eq('season_status', 'current');

  (currentSeasons || []).forEach((s) => map.set(s.club_id, s.id));

  const missing = clubIds.filter((id) => !map.has(id));
  if (missing.length > 0) {
    const { data: fallbackSeasons } = await supabase
      .from('seasons')
      .select('id, club_id, season_number')
      .in('club_id', missing)
      .order('season_number', { ascending: false });

    // Keep the first (highest season_number) row per club.
    (fallbackSeasons || []).forEach((s) => {
      if (!map.has(s.club_id)) map.set(s.club_id, s.id);
    });
  }

  return map;
}

/** Chunk size under typical PostgREST max rows per request when paginating aggregates. */
const PLAYER_GAME_HISTORY_AGG_PAGE_SIZE = 900;

/**
 * All-time win rate per club from mixed and levels doubles in `player_game_history`.
 * Paginates reads so aggregates stay correct beyond default row caps.
 *
 * @returns {Promise<Map<string, number|null>>} club UUID → percentage 0–100 (one decimal) or null if no games
 */
async function fetchPlayerClubWinRatesMap(playerId, clubIds) {
  const uniqueIds = [...new Set(clubIds)].filter(Boolean);
  if (!playerId || !uniqueIds.length) return new Map();

  const aggregates = new Map(uniqueIds.map((id) => [id, { w: 0, played: 0 }]));

  let offset = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('player_game_history')
      .select('club_id, result')
      .eq('player_id', playerId)
      .in('club_id', uniqueIds)
      .in('game_type_id', MIXED_LEVELS_GAME_TYPE_IDS)
      .range(offset, offset + PLAYER_GAME_HISTORY_AGG_PAGE_SIZE - 1);

    if (error) throw error;

    const batch = data || [];
    for (const row of batch) {
      const bucket = aggregates.get(row.club_id);
      if (!bucket) continue;
      bucket.played++;
      if (row.result === 'win') bucket.w++;
    }

    if (batch.length < PLAYER_GAME_HISTORY_AGG_PAGE_SIZE) break;
    offset += PLAYER_GAME_HISTORY_AGG_PAGE_SIZE;
  }

  const out = new Map();
  for (const [clubId, { w, played }] of aggregates) {
    out.set(clubId, winRatePctFromRecord(w, played));
  }
  return out;
}

/**
 * Fast enrichment — win rate only (single batched aggregation). Use on the clubs page
 * where many cards would otherwise trigger per-club leaderboard RPCs.
 *
 * @param {object} baseSnapshot
 * @param {string} viewerPlayerId
 * @returns {Promise<{ clubs: object[] }>}
 */
export async function fetchHomeClubsWinRates(baseSnapshot, viewerPlayerId) {
  const allClubs = baseSnapshot?.clubs || [];
  if (!allClubs.length || !viewerPlayerId) return baseSnapshot;

  const clubIds = allClubs.map((c) => c.id);
  const winRateMap = await fetchPlayerClubWinRatesMap(viewerPlayerId, clubIds).catch((err) => {
    console.warn('fetchPlayerClubWinRatesMap:', err);
    return new Map();
  });

  return {
    clubs: allClubs.map((club) => ({
      ...club,
      clubWinRatePct: winRateMap.get(club.id) ?? null,
    })),
  };
}

/**
 * Enriches the base club snapshot with per-club leaderboard teasers (live Active rank +
 * Active-scoped rank_delta) for the given viewer — same ranking as the club leaderboard tab.
 *
 * Adds `clubWinRatePct` from mixed and levels games in `player_game_history` when available.
 *
 * @param {object} baseSnapshot  Result of fetchHomeClubsSnapshot — { clubs }
 * @param {string} viewerPlayerId
 * @param {'male'|'female'|null} viewerGender  null → Mixed only
 * @returns {Promise<{ clubs: object[] }>}
 */
export async function fetchHomeClubsWithLeaderboardTeasers(baseSnapshot, viewerPlayerId, viewerGender) {
  const allClubs = baseSnapshot?.clubs || [];
  if (!allClubs.length || !viewerPlayerId) return baseSnapshot;

  const clubIds = allClubs.map((c) => c.id);

  const [seasonMap, winRateSnapshot] = await Promise.all([
    batchResolveClubSeasons(clubIds),
    fetchHomeClubsWinRates(baseSnapshot, viewerPlayerId),
  ]);
  const winRateMap = new Map(
    (winRateSnapshot.clubs || []).map((c) => [c.id, c.clubWinRatePct ?? null])
  );

  const teaserResults = await Promise.all(
    allClubs.map(async (club) => {
      const seasonId = seasonMap.get(club.id);
      if (!seasonId) return { clubId: club.id, mixed: null, levels: null, levelsLadder: null };

      try {
        return await fetchViewerActiveLadderTeaser({
          clubId: club.id,
          seasonId,
          viewerPlayerId,
          viewerGender,
        });
      } catch (err) {
        console.warn('fetchViewerActiveLadderTeaser:', club.id, err);
        return { clubId: club.id, seasonId, mixed: null, levels: null, levelsLadder: null };
      }
    })
  );

  const teaserMap = new Map(teaserResults.map((t) => [t.clubId, t]));

  const enrich = (club) => ({
    ...club,
    leaderboardTeaser: teaserMap.get(club.id) || null,
    clubWinRatePct: winRateMap.get(club.id) ?? null,
  });

  return {
    clubs: (baseSnapshot.clubs || []).map(enrich),
  };
}
